import React from 'react';
import { Tag, Flex, Button, Input } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faClose } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import UIContext from 'components/Utils/UIContext';

interface ItemTagsTabProps {
	allTags: Array<{ id: string; name: string }>;
	item: { id: string; taggings?: Array<{ tagId: string }> };
	onUpdateTags: (item: any, tagIds: string[]) => Promise<void>;
	onCreateTag?: (name: string, callback?: (tagId: string) => void) => Promise<void>;
	onClose?: () => void;
	areTagsVisible: boolean;
	setPreferences: (section: string, key: string, value: any) => void;
	scenario?: any;
}

export const ItemTagsTab = ({ allTags, item, onUpdateTags, onCreateTag, onClose, areTagsVisible, setPreferences, scenario }: ItemTagsTabProps) => {
	const { t } = useTranslation();
	const uiContext = React.useContext(UIContext);
	const [enteredTag, setEnteredTag] = React.useState('');

	const assigned: Record<string, boolean> = {};
	(item?.taggings || []).forEach(t => { assigned[t.tagId] = true; });

	const toggle = async (tagId: string) => {
		if (!uiContext.canEdit) return;
		const currentIds = (item?.taggings || []).map(t => t.tagId);
		const newIds = assigned[tagId]
			? currentIds.filter(id => id !== tagId)
			: [...currentIds, tagId];
		await onUpdateTags(item, newIds);
	};

	return (
		<Flex className="mt-2" vertical>
			<Flex className="text-left mb-05">
				<span>{t('pages.navigator.tabs.tags.title')}</span>
				<Button shape="circle" className="mini ml-auto" onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setPreferences('pages', 'navigator.tags', !areTagsVisible);
				}}>
					<FontAwesomeIcon icon={!areTagsVisible ? faChevronDown : faChevronUp} />
				</Button>
				{onClose && (
					<Button shape="circle" variant="solid" className="mini" style={{ marginRight: '-0.9rem' }} onClick={onClose}>
						<FontAwesomeIcon icon={faClose} />
					</Button>
				)}
			</Flex>
			{areTagsVisible && (
				<div className="text-left">
					{(allTags || []).length > 0 ? (
						(allTags || []).map((tag) => (
							<Tag.CheckableTag
								key={tag.id}
								checked={!!assigned[tag.id]}
								onClick={() => toggle(tag.id)}
								className="tag noselection"
							>
								{tag.name}
							</Tag.CheckableTag>
						))
					) : (
						<div style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '0.85rem' }}>
							{t('pages.navigator.no_tag')}
						</div>
					)}
				</div>
			)}
			{uiContext.canEdit && !scenario && areTagsVisible && onCreateTag && (
				<div className="mt-1">
					<Input
						key={item?.id || 'empty'}
						onChange={(e) => setEnteredTag(e.target.value ? e.target.value.slice(0, 30) : '')}
						onKeyDown={async (e) => {
							if (e.key === 'Enter' && enteredTag.trim()) {
								await onCreateTag(enteredTag.trim());
								setEnteredTag('');
							}
						}}
						value={enteredTag}
						className="TagInput"
						placeholder="Create new tags"
					/>
				</div>
			)}
		</Flex>
	);
};

export default ItemTagsTab;
